// analytics: PostHog + GA4, config-driven and inert until the IDs in src/content/analytics.json are filled in.
// - empty key  => that provider does nothing (no script, no network)
// - loaded lazily after first paint + idle, never blocks LCP
// - cookieless PostHog (memory persistence), no autocapture, Do-Not-Track respected
// - everything goes through track(name, props), which fans out to both providers
import cfg from './content/analytics.json';

const ph = cfg.posthog || {};
const ga = cfg.ga4 || {};
const dnt = () => navigator.doNotTrack === '1' || window.doNotTrack === '1' || navigator.msDoNotTrack === '1';
const enabled = () => !dnt() && !!(ph.key || ga.measurementId);

let room = 'city';          // current room id ('city' when outside the tower)
let ready = false;
let scheduled = false;
const queue = [];

export const setRoom = (r) => { room = r || 'city'; };
export const currentRoom = () => room;

function clean(props) {
  const out = {};
  for (const [k, v] of Object.entries(props || {})) {
    if (v == null) continue;
    out[k] = typeof v === 'string' ? v.slice(0, 120) : typeof v === 'number' || typeof v === 'boolean' ? v : String(v).slice(0, 120);
  }
  return out;
}

const bareUrl = (u) => { try { const x = new URL(u, location.href); return x.origin + x.pathname; } catch { return String(u).slice(0, 120); } };

function send(name, props) {
  try { if (ph.key && window.posthog?.capture) window.posthog.capture(name, props); } catch { /* analytics must never break the page */ }
  try { if (ga.measurementId && window.gtag) window.gtag('event', name, props); } catch { /* ignore */ }
}

/** The one entry point: fans out to PostHog and GA4. Safe to call before init; a no-op when no keys. */
export function track(name, props = {}) {
  if (!enabled()) return;
  const p = clean(props);
  if (ready) send(name, p); else if (queue.length < 100) queue.push([name, p]);
}

function loadPostHog() {
  if (!ph.key) return;
  const host = (ph.host || 'https://us.i.posthog.com').replace(/\/$/, '');
  // official array loader stub (https://posthog.com/docs/libraries/js): queues calls until array.js arrives
  const stub = (window.posthog = window.posthog || []);
  if (stub.__SV) return;
  stub._i = [];
  stub.init = (key, conf, name) => { stub._i.push([key, conf, name]); };
  for (const m of ['capture', 'register', 'register_once', 'identify', 'reset', 'opt_out_capturing', 'opt_in_capturing']) {
    stub[m] = (...a) => { stub.push([m, ...a]); };
  }
  stub.__SV = 1;
  const s = document.createElement('script');
  s.async = true; s.crossOrigin = 'anonymous';
  s.src = host.replace('.i.posthog.com', '-assets.i.posthog.com') + '/static/array.js';
  document.head.appendChild(s);
  stub.init(ph.key, {
    api_host: host,
    persistence: 'memory',          // cookieless: nothing stored, no banner
    autocapture: false,
    capture_pageview: true,
    capture_pageleave: false,
    respect_dnt: true,
    disable_session_recording: true,
    disable_surveys: true,
    disable_compression: true,
    person_profiles: 'identified_only',
  });
}

function loadGA4() {
  const id = ga.measurementId;
  if (!id) return;
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() { window.dataLayer.push(arguments); }; // gtag requires the arguments object
  window.gtag('js', new Date());
  window.gtag('config', id, { anonymize_ip: true, allow_google_signals: false, allow_ad_personalization_signals: false });
  const s = document.createElement('script');
  s.async = true; s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
  document.head.appendChild(s);
}

function bindDom() {
  // outbound links (panels, magazines, ticker, hologram bar) and the text-version link
  document.addEventListener('click', (e) => {
    const a = e.target instanceof Element ? e.target.closest('a[href]') : null;
    if (!a) return;
    if (a.id === 'textmode') return track('text_version_click');
    let u; try { u = new URL(a.href, location.href); } catch { return; }
    if (!/^https?:$/.test(u.protocol) || u.host === location.host) return;
    if (a.classList.contains('ticker-link')) track('ticker_click', { url: bareUrl(u.href) });
    track('outbound_click', { url: bareUrl(u.href), room });
  }, true);
  // links opened from code (window.open): dialogue actions, newsroom paper, hologram watch, tower links
  const open = window.open;
  window.open = function (url, ...rest) {
    try {
      const u = new URL(String(url), location.href);
      if (/^https?:$/.test(u.protocol) && u.host !== location.host) track('outbound_click', { url: bareUrl(u.href), room });
    } catch { /* ignore */ }
    return open.call(this, url, ...rest);
  };
}

function start() {
  loadPostHog(); loadGA4(); bindDom();
  ready = true;
  for (const [n, p] of queue.splice(0)) send(n, p);
}

/** Call once, early. Waits for load + first paint + idle, then loads the providers. */
export function initAnalytics() {
  if (scheduled || !enabled()) return;
  scheduled = true;
  const go = () => requestAnimationFrame(() => requestAnimationFrame(() => {
    if ('requestIdleCallback' in window) requestIdleCallback(start, { timeout: 4000 }); else setTimeout(start, 1500);
  }));
  if (document.readyState === 'complete') go(); else addEventListener('load', go, { once: true });
}
